import { useState } from "react"
import { AgentImageIndexBadge } from "@/components/agent/agent-image-attach"
import { ImageLightboxDialog } from "@/components/image-lightbox-dialog"
import { PlayBadge } from "@/components/media/asset-media-overlay"
import { SingleClipPlayer } from "@/components/timeline-player"
import { WorkspaceDialog } from "@/components/workspace/workspace-dialog"
import { useTranslation } from "@/hooks/use-translation"
import type { AgentMessageImage } from "@/lib/agent-api"
import { agentMessageChipPreview } from "@/lib/agent-message-preview"

function isVideoAttachment(image: AgentMessageImage): boolean {
  return image.content_type?.startsWith("video/") === true
}

export function AgentMessageImages({ images, failed }: { images: AgentMessageImage[]; failed?: boolean }) {
  const { t } = useTranslation("workspace")
  const [open, setOpen] = useState<AgentMessageImage | null>(null)
  const [video, setVideo] = useState<AgentMessageImage | null>(null)
  const viewUrl = open?.download_url || open?.thumb_url || null
  const videoUrl = video?.stream_url || video?.download_url || null

  return (
    <>
      <div className="mb-1 flex flex-wrap justify-end gap-1">
        {images.map((image, index) => {
          const chip = agentMessageChipPreview(image)
          if (!chip) return null
          const videoAttachment = isVideoAttachment(image) || chip.kind === "video"
          const token = videoAttachment ? `@vid_${index + 1}` : `@img_${index + 1}`
          return (
            <button
              key={image.id}
              type="button"
              className="relative overflow-hidden rounded-lg bg-ui-surface"
              onClick={() => (videoAttachment ? setVideo(image) : setOpen(image))}
              aria-label={image.filename ? `${image.filename} ${token}` : token}
            >
              {chip.kind === "video" ? (
                <video src={chip.src} muted playsInline preload="metadata" className="h-16 w-16 object-cover" />
              ) : (
                <img src={chip.src} alt="" className="h-16 w-16 object-cover" />
              )}
              {videoAttachment ? (
                <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
                  <PlayBadge className="h-6 w-6 [&_svg]:h-3 [&_svg]:w-3" />
                </span>
              ) : null}
              <AgentImageIndexBadge index={index + 1} />
            </button>
          )
        })}
      </div>
      {failed ? <p className="mb-1 text-right text-[10px] text-ui-text-muted">{t("agent.imageFailed")}</p> : null}
      <ImageLightboxDialog
        open={open != null && viewUrl != null}
        onOpenChange={(next) => {
          if (!next) setOpen(null)
        }}
        url={viewUrl}
        downloadUrl={open?.download_url || viewUrl}
        downloadFilename={open?.filename}
        alt={open?.filename}
        nested
      />
      <WorkspaceDialog
        open={video != null && videoUrl != null}
        onOpenChange={(next) => {
          if (!next) setVideo(null)
        }}
        title={video?.filename || t("agent.videoPreview")}
        closeAriaLabel={t("close", { ns: "common" })}
        size="lg"
        nested
      >
        {video != null && videoUrl != null ? (
          <SingleClipPlayer sourceId={video.id} streamUrl={videoUrl} className="w-full max-w-full" />
        ) : null}
      </WorkspaceDialog>
    </>
  )
}
